/**
 * Calculate tax for a single item based on state codes and tax type
 * @param {Object} item - Cart item
 * @param {string} pharmacyStateCode - Pharmacy's state code
 * @param {string} patientStateCode - Patient's state code
 * @param {string} posTaxType - Tax type (gst or vat)
 * @returns {Object} Tax calculation result
 */
export const calculateItemTax = (item, pharmacyStateCode, patientStateCode, posTaxType) => {
    // Basic fields
    const unitPrice = item.salePrice || 0;
    const quantity = item.quantity || 1;
    const totalAmount = unitPrice * quantity; // This is the anchor (MRP * Qty)
    const itemDiscount = item.discount || 0;

    const netTotalAmount = Math.max(0, totalAmount - itemDiscount);

    // Get tax percentage
    let taxPercentage = item.taxPercentage || 0;
    if (item.batchList) {
        const selectedBatch = item.batchList.find(b => b._id === item.selectedBatchId);
        if (selectedBatch?.taxPercentage) {
            taxPercentage = selectedBatch.taxPercentage;
        }
    }

    // High precision extraction
    // TaxableAmount = TotalAmount / (1 + TaxRate/100)
    const taxableAmount = netTotalAmount / (1 + taxPercentage / 100);
    const totalTax = netTotalAmount - taxableAmount;

    let isIGST = false;
    let IGST = 0, CGST = 0, SGST = 0, VAT = 0;

    const isSameState =
        !patientStateCode ||
        !pharmacyStateCode ||
        pharmacyStateCode === patientStateCode;

    if (posTaxType === "gst") {
        if (isSameState) {
            // Split extracted tax
            CGST = totalTax / 2;
            SGST = totalTax / 2;
            isIGST = false;
        } else {
            IGST = totalTax;
            isIGST = true;
        }
    } else if (posTaxType === "vat") {
        VAT = totalTax;
    }

    return {
        taxPercentage,
        tax: totalTax,
        IGST,
        CGST,
        SGST,
        VAT,
        isIGST,
        IGSTPercentage: isIGST ? taxPercentage : 0,
        CGSTPercentage: !isIGST && posTaxType === "gst" ? taxPercentage / 2 : 0,
        SGSTPercentage: !isIGST && posTaxType === "gst" ? taxPercentage / 2 : 0,
        taxableAmount: taxableAmount,
        itemSubtotal: totalAmount,
        discount: itemDiscount
    };
};


// for return sales,
export const calculateReturnItemTax = (item, pharmacyStateCode, patientStateCode, posTaxType) => {
    // For return items, use the original tax percentages from the sale
    if (item.taxPercentage !== undefined) {
        const unitPrice = item.salePrice || 0;
        const quantity = item.quantity || 1;
        const totalAmount = unitPrice * quantity;

        const taxPercentage = item.taxPercentage || 0;
        const CGSTPercentage = item.CGSTPercentage || 0;
        const SGSTPercentage = item.SGSTPercentage || 0;
        const IGSTPercentage = item.IGSTPercentage || 0;

        const isSameState =
            !patientStateCode ||
            !pharmacyStateCode ||
            pharmacyStateCode === patientStateCode;

        // Extract tax from inclusive price
        const taxableAmount = totalAmount / (1 + taxPercentage / 100);
        const totalTax = totalAmount - taxableAmount;

        let CGST = 0, SGST = 0, IGST = 0, VAT = 0;

        if (posTaxType === "gst") {
            if (isSameState) {
                // Split based on provided percentages if possible, otherwise equal split
                if (CGSTPercentage > 0 || SGSTPercentage > 0) {
                    const totalPercentage = (CGSTPercentage + SGSTPercentage) || 1;
                    CGST = totalTax * (CGSTPercentage / totalPercentage);
                    SGST = totalTax * (SGSTPercentage / totalPercentage);
                } else {
                    CGST = totalTax / 2;
                    SGST = totalTax / 2;
                }
            } else {
                IGST = totalTax;
            }
        } else if (posTaxType === "vat") {
            VAT = totalTax;
        }

        return {
            taxPercentage: taxPercentage,
            CGSTPercentage: CGSTPercentage,
            SGSTPercentage: SGSTPercentage,
            IGSTPercentage: IGSTPercentage,
            tax: totalTax,
            CGST: CGST,
            SGST: SGST,
            IGST: IGST,
            VAT: VAT,
            taxableAmount: taxableAmount,
            isIGST: item.isIGST || false
        };
    }
};



/**
 * Calculate totals for the entire cart including tax
 * @param {Array} cart - Cart items
 * @param {Object} itemTaxes - Calculated taxes for each item
 * @param {string} discountType - Fixed or Percentage
 * @param {number} discountValue - Discount value
 * @param {number} transportCost - Transport cost
 * @returns {Object} Totals object
 */
export const calculateCartTotals = (
    cart,
    itemTaxes,
    discountType,
    discountValue,
    transportCost,
    posTaxType,
    isSameState
) => {
    // 1. Group taxable amounts by tax percentage so that
    //    bill-level discount can be distributed proportionally
    //    and taxes recalculated on taxableAmountAfterDiscount.
    const taxGroups = {};
    let totalTaxableBeforeDiscount = 0;

    cart.forEach((item) => {
        const itemKey = `${item._id}-${item.selectedBatchId}`;
        const taxInfo = itemTaxes[itemKey];
        if (!taxInfo) return;

        const taxRate = taxInfo.taxPercentage || 0;
        const itemTaxableAmount = taxInfo.taxableAmount || 0;

        if (!taxGroups[taxRate]) {
            taxGroups[taxRate] = {
                taxRate,
                taxableAmountBeforeDiscount: 0,
            };
        }

        taxGroups[taxRate].taxableAmountBeforeDiscount += itemTaxableAmount;
        totalTaxableBeforeDiscount += itemTaxableAmount;
    });

    // 2. Calculate total bill discount from Total Taxable Value
    let totalBillDiscount = 0;
    if (discountType === "Fixed") {
        totalBillDiscount = discountValue || 0;
    } else if (discountType === "Percentage") {
        totalBillDiscount = (totalTaxableBeforeDiscount * (discountValue || 0)) / 100;
    }

    // 3. Distribute discount proportionally across tax groups
    //    and recalculate taxes based on taxableAmountAfterDiscount.
    let netTaxableValue = 0;
    let totalTax = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalIGST = 0;
    let totalVAT = 0;

    Object.values(taxGroups).forEach((group) => {
        const { taxRate, taxableAmountBeforeDiscount } = group;

        const proportion =
            totalTaxableBeforeDiscount > 0
                ? taxableAmountBeforeDiscount / totalTaxableBeforeDiscount
                : 0;

        const groupDiscountAmount = (totalBillDiscount || 0) * proportion;
        const netGroupTaxable = Math.max(
            0,
            taxableAmountBeforeDiscount - groupDiscountAmount
        );

        let groupTax = 0;
        let groupCGST = 0;
        let groupSGST = 0;
        let groupIGST = 0;
        let groupVAT = 0;

        if (posTaxType === "gst") {
            if (isSameState) {
                groupTax = (netGroupTaxable * taxRate) / 100;
                groupCGST = groupTax / 2;
                groupSGST = groupTax / 2;
            } else {
                groupTax = (netGroupTaxable * taxRate) / 100;
                groupIGST = groupTax;
            }
        } else if (posTaxType === "vat") {
            groupVAT = (netGroupTaxable * taxRate) / 100;
            groupTax = groupVAT;
        }

        netTaxableValue += netGroupTaxable;
        totalTax += groupTax;
        totalCGST += groupCGST;
        totalSGST += groupSGST;
        totalIGST += groupIGST;
        totalVAT += groupVAT;
    });

    // 4. Grand Total = Net Taxable + Recalculated Tax + Transport
    const grandTotal = netTaxableValue + totalTax + (transportCost || 0);

    return {
        subtotal: totalTaxableBeforeDiscount, // Previous Total Taxable
        discount: totalBillDiscount,
        transport: transportCost || 0,
        taxable: netTaxableValue, // Net Taxable Value
        tax: totalTax,
        CGST: totalCGST,
        SGST: totalSGST,
        IGST: totalIGST,
        VAT: totalVAT,
        netTotal: grandTotal
    };
};



/**
 * Build invoice payload with tax data
 * @param {Object} params - All required parameters
 * @returns {Object} Invoice payload
 */
export const buildInvoicePayloadWithTax = ({
    cart,
    itemTaxes,
    discountType,
    discountValue,
    transportCost,
    paymentData,
    paidType,
    customerType,
    selectedPatient,
    pharmacyPOS,
    posTaxType,
    pharmacyStateCode,
    patientStateCode,
    convertCurrency,
    t
}) => {
    const isSameState =
        !patientStateCode ||
        !pharmacyStateCode ||
        pharmacyStateCode === patientStateCode;

    const totals = calculateCartTotals(
        cart,
        itemTaxes,
        discountType,
        discountValue,
        transportCost,
        posTaxType,
        isSameState
    );

    // Derive a bill-level discount rate so that item-level
    // taxes are also based on taxableAmountAfterDiscount.
    const billDiscountRate =
        totals.subtotal > 0 ? (totals.discount || 0) / totals.subtotal : 0;

    // Find maximum tax percentage for invoice-level data
    let maxTaxPercentage = 0;
    Object.values(itemTaxes).forEach(taxInfo => {
        if (taxInfo.taxPercentage > maxTaxPercentage) {
            maxTaxPercentage = taxInfo.taxPercentage;
        }
    });
    if (paymentData.currency !== pharmacyPOS.baseCurrency) {
        totals.netTotal = convertCurrency(totals.netTotal, pharmacyPOS.baseCurrency, paymentData.currency);
    }
    return {
        clinicId: pharmacyPOS?.hospital_id,
        clinicName: pharmacyPOS?.hospital?.name || "",
        pharmacyId: pharmacyPOS?._id,
        pharmacyName: pharmacyPOS?.facility_name,
        patientId: customerType !== "walking" ? selectedPatient?._id : null,
        isHospitalConnected: customerType === 'hospital_patient',
        patientName: paymentData.patientName || selectedPatient?.name || t("Walk-in Customer"),
        discountType: discountType.toLowerCase(),
        discount: discountValue || 0,
        transportCost: transportCost || 0,
        netTotal: totals.netTotal,
        otherNetTotalPrice: 0,
        currencyType: paymentData.currency || "INR",
        paymentType: paymentData.type,
        isConversion: paymentData.currency !== pharmacyPOS.baseCurrency,
        customerType: customerType,
        totalPaid: Number(paymentData.amount) || 0,
        isPartialPaid: paidType === "partial",
        isPaid: paidType === "full",
        zigConversionValue: paymentData.zigConversionValue || 1,
        isSettled: paymentData.isSettle || false,

        // Tax fields at invoice level
        isIGST: !isSameState && posTaxType === "gst",
        IGST: totals.IGST.toFixed(2),
        taxPercentage: maxTaxPercentage,
        tax: totals.tax.toFixed(2),
        SGST: totals.SGST.toFixed(2),
        CGST: totals.CGST.toFixed(2),
        IGSTPercentage: !isSameState && posTaxType === "gst" ? maxTaxPercentage : 0,
        CGSTPercentage: isSameState && posTaxType === "gst" ? maxTaxPercentage / 2 : 0,
        SGSTPercentage: isSameState && posTaxType === "gst" ? maxTaxPercentage / 2 : 0,

        items: cart.map(item => {
            const itemKey = `${item._id}-${item.selectedBatchId}`;
            const taxInfo = itemTaxes[itemKey] || {};
            const batchData = item.batchList?.find(b => b._id === item.selectedBatchId);

            const taxableBeforeDiscount = taxInfo.taxableAmount || 0;
            const taxableAfterDiscount =
                taxableBeforeDiscount - taxableBeforeDiscount * billDiscountRate;

            const taxRate = taxInfo.taxPercentage || 0;

            let itemTax = 0;
            let itemCGST = 0;
            let itemSGST = 0;
            let itemIGST = 0;
            let itemVAT = 0;

            if (posTaxType === "gst") {
                if (isSameState) {
                    itemTax = (taxableAfterDiscount * taxRate) / 100;
                    itemCGST = itemTax / 2;
                    itemSGST = itemTax / 2;
                } else {
                    itemTax = (taxableAfterDiscount * taxRate) / 100;
                    itemIGST = itemTax;
                }
            } else if (posTaxType === "vat") {
                itemVAT = (taxableAfterDiscount * taxRate) / 100;
                itemTax = itemVAT;
            }

            return {
                medicineQunatity: item.quantity,
                stockId: item.selectedBatchId,
                itemId: item._id,
                batchNumber: item.batchNumber,
                productForm: item.productForm,
                medicineName: item.saltName,
                manufacturer: item.manufacturer,
                discount: item.discount || 0,
                brandName: item.brandName,
                isOTC: item.isOTC || false,

                // Price fields
                MRP: item.salePrice.toFixed(2),
                sellingPrice: item.salePrice.toFixed(2),

                // Tax fields at item level
                isIGST: !isSameState && posTaxType === "gst",
                IGST: itemIGST.toFixed(2),
                taxPercentage: taxInfo.taxPercentage || 0,
                tax: itemTax.toFixed(2),
                SGST: itemSGST.toFixed(2),
                CGST: itemCGST.toFixed(2),
                VAT: itemVAT.toFixed(2),
                IGSTPercentage: !isSameState && posTaxType === "gst" ? (taxInfo.taxPercentage || 0) : 0,
                CGSTPercentage: isSameState && posTaxType === "gst" ? (taxInfo.taxPercentage || 0) / 2 : 0,
                SGSTPercentage: isSameState && posTaxType === "gst" ? (taxInfo.taxPercentage || 0) / 2 : 0,

                // Add batch-level data if available
                // ...(batchData && {
                //   batchTaxPercentage: batchData.taxPercentage,
                //   batchIGST: batchData.IGST,
                //   batchCGST: batchData.CGST,
                //   batchSGST: batchData.SGST,
                //   HSN: batchData.HSN,
                //   MFG: batchData.MFG
                // })
            };
        })
    };
};